/**
 * URL Generator Service
 * Centralized mapping of API endpoint paths for easier reference and maintenance
 * Generated from OpenAPI schema
 */

export const ApiEndpoints = {
  // Admin
  checkAdminAccess: '/Admins',
  
  // Authentication & Sessions
  sessions: '/Sessions',
  login: '/Authentication/login',
  refreshToken: '/Authentication/refresh-token',
  
  // Users
  signUpUser: '/Users',
  getUserById: (userId: string) => `/Users/${userId}`,
  getUserByEmail: (email: string) => `/Users/GetByEmail/${email}`,
  editUser: (userId: string) => `/Users/edit/${userId}`,
  deleteUser: (userId: string) => `/Users/delete/${userId}`,
  
  // Artists
  signUpArtist: '/Artists',
  getArtistInfo: (artistId: string) => `/Artists/${artistId}`,
  createSimpleArtist: '/Artists/marketing',
  searchArtists: '/Artists',
  
  // Artist Follows
  followArtist: (artistId: string) => `/artists/${artistId}/follows`,
  unfollowArtist: (artistId: string) => `/artists/${artistId}/follows`,
  getArtistFollowing: (artistId: string) => `/artists/${artistId}/follows/following`,
  getUserFollowingArtist: (artistId: string, userId: string) => `/artists/${artistId}/follows/users/${userId}/following`,
  getArtistFollowers: (artistId: string) => `/artists/${artistId}/follows/followers`,
  getIsFollowingArtist: (artistId: string) => `/artists/${artistId}/follows/status`,
  updateFollowNotifications: (artistId: string) => `/artists/${artistId}/follows/notifications`,
  getArtistFollowStats: (artistId: string) => `/artists/${artistId}/follows/stats`,
  
  // Artist Hero Section
  getArtistHeroSection: (artistId: string) => `/artists/${artistId}/ArtistHeros`,
  saveArtistHeroSection: (artistId: string) => `/artists/${artistId}/ArtistHeros`,
  updateArtistHeroSection: (artistId: string) => `/artists/${artistId}/ArtistHeros`,
  
  // Artist Images
  uploadArtistImage: (artistId: string) => `/artists/${artistId}/ArtistImages`,
  getArtistImages: (artistId: string) => `/artists/${artistId}/ArtistImages`,
  getArtistImage: (artistId: string, imageId: string) => `/artists/${artistId}/ArtistImages/${imageId}`,
  updateArtistImage: (artistId: string, artistImageId: string) => `/artists/${artistId}/ArtistImages/${artistImageId}`,
  deleteArtistImage: (artistId: string, artistImageId: string) => `/artists/${artistId}/ArtistImages/${artistImageId}`,
  
  // Artist Locations
  getArtistLocations: '/ArtistLocations',
  getArtistLocation: (artistLocationId: string) => `/ArtistLocations/${artistLocationId}`,
  saveArtistLocation: '/ArtistLocations',
  updateArtistLocation: '/ArtistLocations',
  deleteArtistLocation: (artistLocationId: string) => `/ArtistLocations/${artistLocationId}`,
  
  // Artist Tattoo Styles
  getArtistTattooStyles: (artistId: string) => `/artists/${artistId}/tattoo-styles`,
  addTattooStyleToArtist: (artistId: string) => `/artists/${artistId}/tattoo-styles`,
  getArtistTattooStyleAssignment: (artistId: string, artistStyleId: number) => 
    `/artists/${artistId}/tattoo-styles/assignments/${artistStyleId}`,
  updateArtistTattooStyleAssignment: (artistId: string, artistStyleId: number) => 
    `/artists/${artistId}/tattoo-styles/assignments/${artistStyleId}`,
  removeArtistTattooStyleByAssignmentId: (artistId: string, artistStyleId: number) => 
    `/artists/${artistId}/tattoo-styles/assignments/${artistStyleId}`,
  removeArtistTattooStyleByStyleId: (artistId: string, styleId: number) => 
    `/artists/${artistId}/tattoo-styles/styles/${styleId}`,
  
  // Tattoo Categories
  getTattooCategories: '/tattoos/Categories',
  getTattooCategory: (categoryId: number) => `/tattoos/Categories/${categoryId}`,
  getTattooCategoryByName: (name: string) => `/tattoos/Categories/name/${encodeURIComponent(name)}`,
  createTattooCategory: '/tattoos/Categories',
  updateTattooCategory: (categoryId: number) => `/tattoos/Categories/${categoryId}`,
  deleteTattooCategory: (categoryId: number) => `/tattoos/Categories/${categoryId}`,
  
  // Tattoo Styles
  getTattooStyles: '/tattoos/Styles',
  getTattooStylesByCategory: (categoryId: number) => `/tattoos/Styles/category/${categoryId}`,
  getTattooStyle: (styleId: number) => `/tattoos/Styles/${styleId}`,
  getTattooStyleByName: (name: string) => `/tattoos/Styles/name/${encodeURIComponent(name)}`,
  getTattooStyleArtists: (styleId: number) => `/tattoos/Styles/${styleId}/artists`,
  createTattooStyle: '/tattoos/Styles',
  updateTattooStyle: (styleId: number) => `/tattoos/Styles/${styleId}`,
  deleteTattooStyle: (styleId: number) => `/tattoos/Styles/${styleId}`,
  
  // Chat Messages
  getChatMessages: (chatSessionId: string) => `/ChatMessages/${chatSessionId}`,
  sendChatMessage: (chatSessionId: string) => `/ChatMessages/${chatSessionId}`,
  
  // Chat Sessions
  getChatSessions: '/ChatSessions/chats',
  getChatSession: (chatSessionId: string) => `/ChatSessions/${chatSessionId}`,
  createChatSession: '/ChatSessions/create',
  saveChatSession: '/ChatSessions',
  editChatSession: '/ChatSessions/edit',
  deleteChatSession: (chatSessionId: string) => `/ChatSessions/${chatSessionId}`,
  
  // Forms (Team-scoped)
  getForms: (teamId: string) => `/teams/${teamId}/forms/list`,
  getForm: (teamId: string, formId: string) => `/teams/${teamId}/forms/${formId}`,
  getFormSchema: (teamId: string) => `/teams/${teamId}/forms/form-schema`,
  getFormElements: (teamId: string) => `/teams/${teamId}/forms/elements`,
  saveForm: (teamId: string) => `/teams/${teamId}/forms`,
  getFormForEdit: (teamId: string, formId: string) => `/teams/${teamId}/forms/edit/${formId}`,
  updateForm: (teamId: string, formId: string) => `/teams/${teamId}/forms/edit/${formId}`,
  deleteForm: (teamId: string, formId: string) => `/teams/${teamId}/forms/delete/${formId}`,
  
  // Form Enrollments
  getFormEnrollments: (teamId: string) => `/teams/${teamId}/enrollments`,
  getFormEnrollment: (teamId: string, enrollmentId: string) => `/teams/${teamId}/enrollments/${enrollmentId}`,
  getEnrollmentById: (enrollmentId: string) => `/${enrollmentId}`,
  saveFormEnrollment: (teamId: string) => `/teams/${teamId}/enrollments`,
  saveFormEnrollmentForNode: (teamId: string, nodeId: string) => `/teams/${teamId}/enrollments/${nodeId}`,
  deleteFormEnrollment: (teamId: string, enrollmentId: string) => `/teams/${teamId}/enrollments/${enrollmentId}/delete`,
  
  // Form Submissions
  getFormSubmissions: (formId: string) => `/FormSubmissions/${formId}`,
  submitFormSubmission: '/FormSubmissions',
  
  // Locations
  searchLocations: '/Locations',
  getLocation: (locationId: string) => `/Locations/${locationId}`,
  saveLocation: '/Locations',
  updateLocation: '/Locations',
  deleteLocation: (locationId: string) => `/Locations/${locationId}`,
  
  // Projects
  getProjects: '/Projects',
  getProjectsByArtistTeam: (artistId: string, teamId: string) => `/artists/${artistId}/teams/${teamId}/Projects`,
  getProjectsByUser: (userId: string) => `/users/${userId}/Projects`,
  getProject: (projectId: string) => `/Projects/${projectId}`,
  getProjectByArtistTeam: (artistId: string, teamId: string, projectId: string) => 
    `/artists/${artistId}/teams/${teamId}/Projects/${projectId}`,
  getProjectByUser: (userId: string, projectId: string) => `/users/${userId}/Projects/${projectId}`,
  createProject: '/Projects',
  createProjectForArtistTeam: (artistId: string, teamId: string) => `/artists/${artistId}/teams/${teamId}/Projects`,
  createProjectForUser: (userId: string) => `/users/${userId}/Projects`,
  updateProject: (projectId: string) => `/Projects/${projectId}`,
  updateProjectByArtistTeam: (artistId: string, teamId: string, projectId: string) => 
    `/artists/${artistId}/teams/${teamId}/Projects/${projectId}`,
  updateProjectByUser: (userId: string, projectId: string) => `/users/${userId}/Projects/${projectId}`,
  
  // Project Status
  getProjectStatus: (projectId: string) => `/projects/${projectId}/ProjectStatus`,
  updateProjectStatus: (projectId: string) => `/projects/${projectId}/ProjectStatus`,
  
  // Member Votes
  getMemberVotes: (projectId: string, teamReviewId: string) => 
    `/projects/${projectId}/teamreviews/${teamReviewId}/MemberVotes`,
  submitTeamMemberReviewVote: (projectId: string, teamReviewId: string) => 
    `/projects/${projectId}/teamreviews/${teamReviewId}/MemberVotes`,
  
  // Nodes (Workflow)
  getNodes: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}/Nodes`,
  getNode: (teamId: string, workflowId: string, nodeId: string) => 
    `/teams/${teamId}/workflows/${workflowId}/Nodes/${nodeId}`,
  getNodeForEdit: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}/Nodes/edit`,
  saveNode: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}/Nodes`,
  updateNode: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}/Nodes/Edit`,
  deleteNode: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}/Nodes`,
  
  // Teams
  getTeam: '/Teams',
  getTeamMembers: (teamId: string) => `/teams/${teamId}/TeamMembers`,
  removeTeamMember: (teamId: string) => `/teams/${teamId}/TeamMembers`,
  
  // Team Invites
  getTeamInvites: (teamId: string) => `/teams/${teamId}/TeamInvites`,
  createTeamInvite: (teamId: string) => `/teams/${teamId}/TeamInvites`,
  acceptTeamInvite: (teamId: string) => `/teams/${teamId}/TeamInvites/accept`,
  deleteTeamInvite: (teamId: string, inviteId: string) => `/teams/${teamId}/TeamInvites/${inviteId}`,
  
  // Team Reviews
  getTeamReview: (projectId: string, teamReviewId: string) => `/projects/${projectId}/TeamReviews/${teamReviewId}`,
  deleteTeamReview: (projectId: string, teamReviewId: string) => `/projects/${projectId}/TeamReviews/${teamReviewId}`,
  isTeamReviewComplete: (projectId: string, teamReviewId: string) => 
    `/projects/${projectId}/TeamReviews/${teamReviewId}/is-complete`,
  
  // Team Review Settings
  getTeamReviewSettings: (teamId: string) => `/teams/${teamId}/TeamReviewSettings`,
  createTeamReviewSettings: (teamId: string) => `/teams/${teamId}/TeamReviewSettings`,
  updateTeamReviewSettings: (teamId: string) => `/teams/${teamId}/TeamReviewSettings`,
  deleteTeamReviewSettings: (teamId: string, teamReviewSettingsId: string) => 
    `/teams/${teamId}/TeamReviewSettings/${teamReviewSettingsId}`,
  
  // Workflows
  getWorkflows: (teamId: string) => `/teams/${teamId}/workflows/list`,
  getWorkflow: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}`,
  insertWorkflow: (teamId: string) => `/teams/${teamId}/workflows/create`,
  updateWorkflow: (teamId: string) => `/teams/${teamId}/workflows/edit`,
  deleteWorkflow: (teamId: string, workflowId: string) => `/teams/${teamId}/workflows/${workflowId}`,
  
  // Waiting List
  getWaitingList: '/WaitingList',
  addToWaitingList: '/WaitingList',
} as const;

export default ApiEndpoints;
